import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody } from "reactstrap";
import Axios from 'axios';
import { Auth } from "aws-amplify";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Skeleton from 'react-loading-skeleton';
import Head from 'src/layout/head/Head';
import { Modal, ModalBody, ModalHeader, Button, Spinner, ModalFooter, CardHeader, CardTitle } from "reactstrap";

import { PlusCircleIcon, XMarkIcon, CheckCircleIcon, UserIcon, XCircleIcon, PencilIcon } from '@heroicons/react/24/outline';

import TraveltekLogo from '../../integrations/images/traveltek.png'

import API_ROUTES from "src/config/api";

import 'react-loading-skeleton/dist/skeleton.css'

const Traveltek = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sitename, setSitename] = useState('');
  const [bookingPrefix, setBookingPrefix] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const [loading, setLoading] = useState(true);

  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const { organisation } = useSelector((state) => state);

  /**
   * Toggle the modal for adding/editing a Traveltek connection
   */
  const toggleModal = (editing = false) => {
    setErrors({});
    setIsEditing(editing);

    if (editing && config) {
      setUsername(config.username);
      setPassword('');
      setSitename(config.sitename || '');
      setBookingPrefix(config.booking_prefix || '');
      setEnabled(config.enabled);
    } else {
      setUsername('');
      setPassword('');
      setSitename('');
      setBookingPrefix('');
      setEnabled(true);
    }

    setModalIsOpen(!modalIsOpen);
  }

  /**
   * Handle Form Submission from the modal
   */
  const handleSubmit = async () => {
    setErrors({});
    setIsSubmitting(true);

    let returnedErrors = {};
    if (username.length < 1) {
      returnedErrors.username = 'Username is required';
    }

    if (!isEditing && password.length < 1) {
      returnedErrors.password = 'Password is required';
    }

    if (Object.keys(returnedErrors).length > 0) {
      setErrors(returnedErrors);
      setIsSubmitting(false);
      return;
    }

    const user = await Auth.currentAuthenticatedUser();

    try {
      if (isEditing && config) {
        const updateData = {
          username: username,
          enabled: enabled,
          sitename: sitename || null,
          booking_prefix: bookingPrefix || null,
        };

        if (password.length > 0) {
          updateData.password = password;
        }

        await Axios(
          API_ROUTES.reporting['integration:traveltek:patch'](config.id),
          {
            method: 'patch',
            data: updateData,
            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          }
        );
      } else {
        await Axios(
          API_ROUTES.reporting['integration:traveltek:post'](),
          {
            method: 'post',
            data: {
              organisation_id: organisation.organisation,
              username: username,
              password: password,
              sitename: sitename || null,
              booking_prefix: bookingPrefix || null,
              enabled: enabled,
            },
            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          }
        );
      }

      setModalIsOpen(false);
      setIsSubmitting(false);
      getConfig();
    } catch (error) {
      setIsSubmitting(false);
      if (error.response?.status === 409) {
        setErrors({ general: 'A Traveltek configuration already exists for this organisation' });
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    }
  }

  const toggleEnabled = async () => {
    if (!config) return;

    setLoading(true);
    const user = await Auth.currentAuthenticatedUser();

    try {
      await Axios(
        API_ROUTES.reporting['integration:traveltek:patch'](config.id),
        {
          method: 'patch',
          data: {
            enabled: !config.enabled,
          },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );
      getConfig();
    } catch (error) {
      setLoading(false);
    }
  }

  const getConfig = useCallback(async () => {
    setLoading(true);

    if (!organisation?.organisation) {
      return null;
    }

    const user = await Auth.currentAuthenticatedUser();

    try {
      const result = await Axios(
        API_ROUTES.reporting['integration:traveltek:get'](organisation.organisation),
        {
          method: 'get',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );

      setConfig(result.data.data.data);
      setLogs(result.data.data.log || []);
    } catch (error) {
      setConfig(null);
      setLogs([]);
    }

    setLoading(false);
  }, [organisation.organisation]);

  useEffect(() => {
    getConfig();
  }, [organisation.organisation]);

  if (loading === true) {
    return (
      <React.Fragment>

        <Head title="Traveltek Integration" />

        <div className="mb-4">
          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>

      <Head title="Traveltek Integration" />

      <div className="mb-4">

        {config?.id && (
          <React.Fragment>
          <Card className="relative border-primary/10 border border-slate-300 rounded-lg overflow-hidden w-full">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-b from-primary/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />

            <CardHeader className="py-2 bg-slate-50">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-lg text-primary mb-0">Traveltek <strong>Connection</strong></CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className='bg-violet-800 border-violet-800 rounded-md'
                    onClick={() => toggleModal(true)}
                  >
                    <PencilIcon className="h-4 w-4 inline mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    className={`rounded-md ${config.enabled ? 'bg-red-600 border-red-600' : 'bg-green-600 border-green-600'}`}
                    onClick={toggleEnabled}
                  >
                    {config.enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardBody className='pb-10 pt-4'>
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm clear-both mx-1">
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Username</p>
                    <div className="flex items-center gap-1.5">
                      <UserIcon className="h-4 w-4" />
                      <span>{config.username}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Sitename</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-600">{config.sitename || '-'}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Booking Prefix</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-600">{config.booking_prefix || '-'}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Status</p>
                    {config.enabled && (
                      <div className="flex items-center gap-1.5 text-emerald-700">
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>Enabled</span>
                      </div>
                    )}
                    {!config.enabled && (
                      <div className="flex items-center gap-1.5 text-red-500">
                        <XCircleIcon className="h-4 w-4" />
                        <span>Disabled</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Sync Logs */}
          <Card className="mt-4 border border-slate-300 rounded-lg overflow-hidden w-full">
            <CardHeader className="py-3 bg-slate-50">
              <CardTitle className="font-normal text-lg text-primary mb-0">Transaction <strong>Synchronisation Logs</strong></CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              {logs.length === 0 ? (
                <div className="py-8 text-center text-slate-500 text-sm">
                  No sync logs yet. Logs will appear here once transactions are processed.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Transaction ID</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">Checked</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">Found</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, index) => (
                        <tr key={log.transaction_id || index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            <Link to={`/transaction/${log.transaction_id}`} className="text-violet-700 hover:text-violet-900 underline">
                              {log.transaction_id}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {log.checked ? (
                              <CheckCircleIcon className="h-5 w-5 text-emerald-600 inline" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-slate-300 inline" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {log.found ? (
                              <CheckCircleIcon className="h-5 w-5 text-emerald-600 inline" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-400 inline" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {log.updated ? (
                              <CheckCircleIcon className="h-5 w-5 text-emerald-600 inline" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-slate-300 inline" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
          </React.Fragment>
        )}

        {!config?.id && (
          <Card
            className="border-dashed border-2 border-muted-foreground/20 bg-muted/50 mt-0 cursor-pointer w-full"
            onClick={() => toggleModal(false)}
          >
            <CardBody className="flex flex-col items-center justify-center text-center py-16 px-6">
              <PlusCircleIcon className="!h-10 !w-10 block mb-2 text-violet-800" />
              <h3 className="font-semibold text-muted-foreground text-violet-800">Add Traveltek Connection</h3>
              <p className="text-sm text-muted-foreground mt-1">Connect your Traveltek account</p>
            </CardBody>
          </Card>
        )}

      </div>

      <Modal isOpen={modalIsOpen} toggle={() => toggleModal(false)} >
        <ModalHeader
          toggle={() => toggleModal(false)}
          className="bg-secondary text-white text-2xl"
          close={
            <button className="close !-mt-[12px]" onClick={() => toggleModal(false)}>
              <XMarkIcon className="!h-6 !w-6 text-white mt-[2px]" />
            </button>
          }
        >
          <img src={TraveltekLogo} alt="Traveltek Logo" className="h-8 inline -ml-3 mr-1" />
          {isEditing ? 'Edit Connection' : 'New Connection'}
        </ModalHeader>
        <ModalBody className="modal-body-xxl text-center px-4">
          <div className="nk-modal">
            <div className="text-muted-foreground">
              <p className="mb-4 text-left text-sm">
                {isEditing
                  ? 'Update your Traveltek credentials below.'
                  : 'Please enter your Traveltek credentials to create a connection.'}
              </p>
            </div>

            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm text-left">
                {errors.general}
              </div>
            )}

            <div className="mb-4">

              <div className='text-left mb-4'>
                <p className="text-muted-foreground mb-1 text-left text-slate-800 font-bold text-xs">Username</p>
                <input type="text" placeholder="Username" className={`py-4 form-control ${errors.username ? 'is-invalid' : ''}`} value={username} onChange={(e) => setUsername(e.target.value)} />
                {errors.username && (
                  <p className="text-danger text-xs">{errors.username}</p>
                )}
                {!errors.username && (
                  <p className="text-muted-foreground text-xs">Your Traveltek API username</p>
                )}
              </div>

              <div className='text-left mb-4'>
                <p className="text-muted-foreground mb-1 text-left text-slate-800 font-bold text-xs">Password</p>
                <input type="password" placeholder={isEditing ? "Leave blank to keep current password" : "Password"} className={`py-4 form-control ${errors.password ? 'is-invalid' : ''}`} value={password} onChange={(e) => setPassword(e.target.value)} />
                {errors.password && (
                  <p className="text-danger text-xs">{errors.password}</p>
                )}
                {!errors.password && (
                  <p className="text-muted-foreground text-xs">
                    {isEditing ? 'Leave blank to keep current password' : 'Your Traveltek API password'}
                  </p>
                )}
              </div>

              <div className='text-left mb-4'>
                <p className="text-muted-foreground mb-1 text-left text-slate-800 font-bold text-xs">Sitename <span className="font-normal">(optional)</span></p>
                <input type="text" placeholder="e.g. felloh.site.traveltek.net" className="py-4 form-control" value={sitename} onChange={(e) => setSitename(e.target.value)} />
                <p className="text-muted-foreground text-xs">Your Traveltek sitename</p>
              </div>

              <div className='text-left mb-4'>
                <p className="text-muted-foreground mb-1 text-left text-slate-800 font-bold text-xs">Booking Prefix <span className="font-normal">(optional)</span></p>
                <input type="text" placeholder="e.g. OTC-" className="py-4 form-control" value={bookingPrefix} onChange={(e) => setBookingPrefix(e.target.value)} />
                <p className="text-muted-foreground text-xs">Booking reference prefix used to identify Traveltek bookings</p>
              </div>

              <div className='text-left'>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm text-slate-800">Enable integration immediately</span>
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="bg-light !py-4">
          <Button
            className="bg-violet-800 border-violet-800 text-white cursor-pointer"
            type="submit"
            size="lg"
            disabled={isSubmitting === true}
            onClick={() => handleSubmit()}
          >
            {isSubmitting ? <Spinner size="sm" color="light"> </Spinner> : (isEditing ? 'Update Connection' : 'Create Connection')}
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  )
};

export default Traveltek;
